/**
 * LiveInventory — reusable live vehicle-card strip fed by /api/vehicles.
 * Used on Home, city landing pages, Shop-by-Need, and EV pages so no public
 * page ever shows static/demo vehicle cards.
 *
 * When no vehicles match, shows the spec'd fallback message + CTAs instead
 * of fake cars.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, MessageSquareText, Search } from "lucide-react";
import VehicleCard from "./VehicleCard";
import { getVehicles } from "../lib/api";
import { siteConfig } from "../config";

export default function LiveInventory({
  title,
  subtitle,
  params = {},
  limit = 4,
  viewAllTo = "/inventory",
  viewAllLabel = "View All Inventory",
  testId = "live-inventory",
  columns = 4,
}) {
  const [items, setItems] = useState(null);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let on = true;
    setItems(null);
    getVehicles({ sort: "featured", limit, ...params })
      .then((d) => on && setItems(d.items || []))
      .catch(() => on && setItems([]));
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, limit]);

  const gridCols =
    columns === 3
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  return (
    <div data-testid={testId}>
      {(title || subtitle) && (
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            {title && (
              <h2 className="ssc-display text-2xl md:text-3xl font-black text-[var(--ssc-navy)]">{title}</h2>
            )}
            {subtitle && <p className="mt-1 text-sm text-[var(--ssc-ink-2)] max-w-2xl">{subtitle}</p>}
          </div>
          {items && items.length > 0 && (
            <Link
              to={viewAllTo}
              className="inline-flex items-center gap-1 text-[var(--ssc-blue)] font-extrabold text-sm hover:underline shrink-0"
              data-testid={`${testId}-view-all`}
            >
              {viewAllLabel} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      )}

      {items === null ? (
        <div className="flex items-center gap-2 text-[var(--ssc-ink-2)] py-8" data-testid={`${testId}-loading`}>
          <Loader2 className="animate-spin" size={18} /> Loading live inventory…
        </div>
      ) : items.length === 0 ? (
        <div className="ssc-card text-center py-8" data-testid={`${testId}-empty`}>
          <div className="text-lg font-extrabold text-[var(--ssc-navy)]">
            No matching vehicles are available right now, but I can help you find one.
          </div>
          <p className="mt-2 text-[var(--ssc-ink-2)] text-sm">
            Text Scott and I'll check incoming inventory.
          </p>
          <div className="mt-4 flex justify-center gap-3 flex-wrap">
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid={`${testId}-text-scott`}>
              <MessageSquareText size={16} /> Text Scott
            </a>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-navy" data-testid={`${testId}-find-me`}>
              Find Me a Vehicle
            </Link>
            <Link to={viewAllTo} className="ssc-btn ssc-btn-outline" data-testid={`${testId}-view-all-empty`}>
              <Search size={16} /> View All Inventory
            </Link>
          </div>
        </div>
      ) : (
        <div className={`grid ${gridCols} gap-5`} data-testid={`${testId}-grid`}>
          {items.map((v) => (
            <VehicleCard key={v.id || v.vin} vehicle={v} />
          ))}
        </div>
      )}
    </div>
  );
}
